import React, { useEffect, useMemo, useState } from "react";
import { authFetch, isAdmin, isOperator } from "../../auth";
import { ActionModal } from "../Common/ActionModal";
import { usePermission } from "../Auth/PermissionContext";
import { PermissionKeys } from "../../permissions";

function colorPickerValue(value, fallback = "#1677ff") {
  const raw = String(value || "").trim();
  return /^#[0-9a-fA-F]{6}$/.test(raw) ? raw : fallback;
}

export function GroupManagement() {
  const admin = isAdmin();
  const operator = isOperator();
  const { can } = usePermission();
  const canView = can(PermissionKeys.GroupView);
  const canCreate = can(PermissionKeys.GroupCreate);
  const canUpdate = can(PermissionKeys.GroupUpdate);
  const canDelete = can(PermissionKeys.GroupDelete);

  const [name, setName] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerError, setCustomerError] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("");
  const [newCustomerId, setNewCustomerId] = useState("");

  const [editModal, setEditModal] = useState({ open: false, row: null, name: "", color: "" });
  const [deleteModal, setDeleteModal] = useState({ open: false, row: null });
  const [modalSubmitting, setModalSubmitting] = useState(false);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);

  useEffect(() => {
    if (!admin) return;
    loadCustomerOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  useEffect(() => {
    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  async function loadCustomerOptions() {
    try {
      const res = await authFetch("/api/customers/options");
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "客户列表加载失败");
      }

      const data = await res.json();
      setCustomers(Array.isArray(data?.items) ? data.items : []);
      setCustomerError("");
    } catch (err) {
      setCustomers([]);
      setCustomerError(err?.message || "客户列表加载失败");
    }
  }

  async function loadGroups(resetMsg = false) {
    setLoading(true);
    setError("");
    if (resetMsg) setMessage("");

    try {
      const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (name.trim()) qs.set("name", name.trim());
      if (admin && customerId.trim()) qs.set("customerId", customerId.trim());

      const res = await authFetch(`/api/groups?${qs.toString()}`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "分组查询失败");
      }

      const data = await res.json();
      setItems(data?.items || []);
      setTotal(Number(data?.total || 0));
    } catch (err) {
      setError(err?.message || "分组查询失败");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  function onSearch(e) {
    e.preventDefault();
    setPage(1);
    setTimeout(() => loadGroups(true), 0);
  }

  async function onCreateGroup(e) {
    e.preventDefault();
    if (operator) return;

    setError("");
    setMessage("");

    if (admin && !newCustomerId.trim()) {
      setError("客户不能为空。");
      return;
    }

    const payload = {
      name: newName.trim(),
      color: newColor.trim() || null,
      customerId: admin && newCustomerId.trim() ? Number(newCustomerId.trim()) : null
    };

    const res = await authFetch("/api/groups", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      const txt = await res.text();
      setError(txt || "创建分组失败");
      return;
    }

    setMessage("分组创建成功。");
    setNewName("");
    setNewColor("");
    setNewCustomerId("");
    await loadGroups();
  }

  function openRenameModal(row) {
    if (operator || !canUpdate) return;
    setEditModal({ open: true, row, name: row.name || "", color: row.color || "" });
  }

  async function submitRename(form) {
    const row = editModal.row;
    if (!row) return;

    const nextName = (form.name || "").trim();
    if (!nextName) {
      setError("分组名称不能为空。");
      return;
    }

    setModalSubmitting(true);
    setError("");
    setMessage("");

    try {
      const res = await authFetch(`/api/groups/${row.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: nextName, color: (form.color || "").trim() || null })
      });

      if (!res.ok) {
        const txt = await res.text();
        setError(txt || "更新分组失败");
        return;
      }

      setEditModal({ open: false, row: null, name: "", color: "" });
      setMessage(`分组 ${row.name} 已更新。`);
      await loadGroups();
    } finally {
      setModalSubmitting(false);
    }
  }

  function openDeleteModal(row) {
    if (operator || !canDelete) return;
    setDeleteModal({ open: true, row });
  }

  async function submitDelete() {
    const row = deleteModal.row;
    if (!row) return;

    setModalSubmitting(true);
    setError("");
    setMessage("");

    try {
      const res = await authFetch(`/api/groups/${row.id}`, { method: "DELETE" });
      if (!res.ok) {
        const txt = await res.text();
        setError(txt || "删除分组失败");
        return;
      }

      setDeleteModal({ open: false, row: null });
      setMessage(`分组 ${row.name} 已删除。`);
      await loadGroups();
    } finally {
      setModalSubmitting(false);
    }
  }

  if (operator || !canView) {
    return (
      <div className="container-fluid">
        <div className="status-banner warn mt-3">当前账号无车辆分组管理权限。</div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card shadow-sm status-card mt-3">
        <div className="card-header bg-primary text-white">车辆分组管理</div>
        <div className="card-body">
          <form className="row g-2 align-items-end" onSubmit={onSearch}>
            <div className="col-md-4">
              <label className="form-label">分组名</label>
              <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            {admin && (
              <div className="col-md-3">
                <label className="form-label">客户</label>
                <select className="form-select" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                  <option value="">全部客户</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={String(customer.id)}>
                      {customer.name}{customer.code ? ` / ${customer.code}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-md-2">
              <button className="btn btn-primary w-100" type="submit" disabled={loading}>
                {loading ? "查询中..." : "查询"}
              </button>
            </div>
          </form>

          {error && <div className="status-banner error mt-3">{error}</div>}
          {customerError && <div className="status-banner warn mt-3">{customerError}</div>}
          {message && <div className="status-banner info mt-3">{message}</div>}

          <div className="table-responsive mt-3">
            <table className="table table-sm table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>分组名</th>
                  <th>颜色</th>
                  <th>客户</th>
                  <th>设备数</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={6}><div className="status-banner empty m-0">暂无分组数据</div></td>
                  </tr>
                )}
                {items.map((row) => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.name}</td>
                    <td>
                      {row.color ? (
                        <span className="d-inline-flex align-items-center gap-2">
                          <span
                            className="d-inline-block rounded border"
                            style={{ width: 18, height: 18, backgroundColor: row.color }}
                          />
                          {row.color}
                        </span>
                      ) : "-"}
                    </td>
                    <td>{row.customerName || `#${row.customerId}`}</td>
                    <td>{row.deviceCount}</td>
                    <td>
                      <div className="d-flex gap-1 flex-wrap">
                        {canUpdate && (
                          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => openRenameModal(row)}>
                            重命名
                          </button>
                        )}
                        {canDelete && (
                          <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => openDeleteModal(row)}>
                            删除
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="text-muted" style={{ fontSize: 13 }}>
              共 {total} 条，当前第 {page}/{pageCount} 页
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary btn-sm" type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>上一页</button>
              <button className="btn btn-outline-secondary btn-sm" type="button" disabled={page >= pageCount || loading} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>下一页</button>
            </div>
          </div>

          {canCreate && (
            <>
              <hr className="my-4" />
              <h6 className="mb-3">新增分组</h6>
              <form className="row g-2 align-items-end" onSubmit={onCreateGroup}>
                <div className="col-md-3">
                  <label className="form-label">分组名</label>
                  <input className="form-control" value={newName} onChange={(e) => setNewName(e.target.value)} required />
                </div>
                <div className="col-md-3">
                  <label className="form-label">颜色</label>
                  <div className="input-group">
                    <input
                      className="form-control"
                      value={newColor}
                      onChange={(e) => setNewColor(e.target.value)}
                      placeholder="#1677ff"
                    />
                    <input
                      type="color"
                      className="form-control form-control-color"
                      value={colorPickerValue(newColor)}
                      aria-label="颜色调色板"
                      onChange={(e) => setNewColor(e.target.value)}
                    />
                  </div>
                </div>
                {admin && (
                  <div className="col-md-3">
                    <label className="form-label">客户</label>
                    <select className="form-select" value={newCustomerId} onChange={(e) => setNewCustomerId(e.target.value)} required>
                      <option value="">请选择客户</option>
                      {customers.map((customer) => (
                        <option key={customer.id} value={String(customer.id)}>
                          {customer.name}{customer.code ? ` / ${customer.code}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="col-md-3">
                  <button className="btn btn-success w-100" type="submit">创建分组</button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      <ActionModal
        open={editModal.open && canUpdate}
        title="编辑分组"
        message={editModal.row ? `分组：${editModal.row.name}` : ""}
        fields={[
          { name: "name", label: "分组名称", required: true },
          { name: "color", label: "颜色", type: "colorText", placeholder: "如 #1677ff" }
        ]}
        initialValues={{ name: editModal.name, color: editModal.color }}
        submitting={modalSubmitting}
        onCancel={() => setEditModal({ open: false, row: null, name: "", color: "" })}
        onConfirm={submitRename}
      />

      <ActionModal
        open={deleteModal.open && canDelete}
        title="删除分组"
        message={deleteModal.row ? `确认删除分组：${deleteModal.row.name} 吗？` : ""}
        fields={[]}
        initialValues={{}}
        confirmText="确认删除"
        confirmClassName="btn-danger"
        submitting={modalSubmitting}
        onCancel={() => setDeleteModal({ open: false, row: null })}
        onConfirm={submitDelete}
      />
    </div>
  );
}
